import { useTranslation } from "react-i18next";

import { LoadingButton } from "@mui/lab";
import { Button, Typography, styled } from "@mui/material";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { CheckboxWithLabel } from "formik-mui";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { NavLink } from "../../components/navigation/NavLink";
import { useConfig } from "../../hooks/settingHooks";
import { useRegistration } from "../../hooks/usersHooks";
import { useErrorSnackbar } from "../../hooks/errorHooks";
import { TextField } from "../../components/form/TextField";

interface Props {}

interface RegisterForm {
  username: string;
  password: string;
  gdprAccepted: boolean;
}

export function RegisterPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = useConfig();
  const { mutateAsync: register } = useRegistration(config);
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const initialValues: RegisterForm = {
    username: "",
    password: "",
    gdprAccepted: false,
  };
  const validationScheme = Yup.object().shape({
    username: Yup.string()
      .email(t("validation.invalidEmail"))
      .required(t("validation.requiredField", { field: t("label.userName") })),
    password: Yup.string().required(
      t("validation.requiredField", { field: t("label.password") })
    ),
    gdprAccepted: Yup.boolean().oneOf([true], t("validation.requiredToAgree")),
  });

  async function onSubmit(
    values: RegisterForm,
    helpers: FormikHelpers<RegisterForm>
  ) {
    const { setSubmitting } = helpers;
    const { username, password, gdprAccepted } = values;
    setSubmitting(true);
    try {
      // await registerAPI.request(username, password, {
      //   gdprAccepted: gdprAccepted,
      //   lastRegistrationConfig: config,
      // });
      await register({
        email: username,
        password,
        metadata: {
          gdprAccepted: gdprAccepted,
          lastRegistrationConfig: config,
        },
      });

      navigate("../login");
    } catch (errors) {
      enqueueErrorSnackbar(errors);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationScheme}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <StyledForm>
          <Typography variant="h3" component="h3" color="textSecondary">
            {t("label.register")}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h4"
            color="textSecondary"
            className="subtitle"
          >
            {t("label.chooseYourUsernameAndPasswordToContiue")}
          </Typography>
          <Field
            component={TextField}
            autoFocus
            className="login-field"
            name="username"
            type="email"
            label={t("label.userName")}
            disabled={isSubmitting}
          />
          <Field
            component={TextField}
            className="login-field"
            name="password"
            type="password"
            label={t("label.password")}
            disabled={isSubmitting}
          />
          <Field
            component={CheckboxWithLabel}
            className="login-field"
            name="gdprAccepted"
            type="checkbox"
            Label={{ label: t("label.gdprAccepted") }}
            disabled={isSubmitting}
          />
          <div className="button-group">
            <LoadingButton
              className="submit"
              variant="contained"
              loading={isSubmitting}
              type="submit"
            >
              {t("label.register")}
            </LoadingButton>
            <Button
              component={NavLink}
              to={`..`}
              variant="text"
              disabled={isSubmitting}
            >
              {t("label.cancel")}
            </Button>
          </div>
        </StyledForm>
      )}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  .button-group {
    > * + * {
      margin-left: ${({ theme }) => `${theme.spacing(1)}`};
    }
  }

  .login-field,
  .subtitle,
  .button-group {
    margin-bottom: ${({ theme }) => `${theme.spacing(3)}`};
  }
`;
